/**
 * Egress Cost Predictor — v32.2 customer-gap fill
 * ================================================
 * Free public tool. Engineer picks source-cloud × dest-cloud × payload-size
 * × model and gets a pre-flight verdict: "MOVE THE MODEL" / "REDUCE THE
 * PAYLOAD" / "OK". Hardcoded egress rates (transparency via /api/egress/rates).
 *
 * Brand: dark marketing tier. Emerald primary, amber + rose for verdicts.
 */
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Link } from 'react-router-dom';
// v34.55 (Feb 2026) — promoted from BackToHomePill (small floating pill that's
// easy to miss) to full PublicNavbar so visitors get the same top nav as
// /pilot, /wall-of-tenants, /slack-bot, /cli, /savings-calculator, etc.
import PublicNavbar from '../components/PublicNavbar';
import { useAuth } from '../context/AuthContext';
import {
  Network, ArrowRight, AlertOctagon, AlertTriangle, CheckCircle2, Loader2,
  Database, Cpu, Calculator, DollarSign,
} from 'lucide-react';
import { ENGINE_COUNT } from '../constants/product';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERDICT_STYLES = {
  rose: { card: 'border-rose-500/40 bg-rose-500/10', text: 'text-rose-300', label: 'text-rose-400', Icon: AlertOctagon },
  amber: { card: 'border-amber-500/40 bg-amber-500/10', text: 'text-amber-300', label: 'text-amber-400', Icon: AlertTriangle },
  emerald: { card: 'border-emerald-500/40 bg-emerald-500/10', text: 'text-emerald-300', label: 'text-emerald-400', Icon: CheckCircle2 },
};

const PRESETS = [
  { label: 'GCP Snowflake → AWS Bedrock (1 TB embedding pass)', cfg: { source_cloud: 'gcp', dest_cloud: 'aws', data_gb: 1024, model: 'gemini-3-flash', monthly_runs: 4, inference_token_budget_pct: 0.05 } },
  { label: 'Snowflake → GCP Vertex (500 GB rerank, 10×/mo)',   cfg: { source_cloud: 'snowflake', dest_cloud: 'gcp', data_gb: 500, model: 'gemini-3-pro', monthly_runs: 10, inference_token_budget_pct: 1.0 } },
  { label: 'AWS RDS → Claude Sonnet (200 GB nightly summary)', cfg: { source_cloud: 'aws', dest_cloud: 'aws', data_gb: 200, model: 'claude-sonnet-4-5', monthly_runs: 30, inference_token_budget_pct: 5 } },
  { label: 'Azure Data Lake → OpenAI (100 GB, 50 runs/mo)',    cfg: { source_cloud: 'azure', dest_cloud: 'aws', data_gb: 100, model: 'gpt-5.2', monthly_runs: 50, inference_token_budget_pct: 2 } },
];

const EgressPredictor = () => {
  const { user } = useAuth(); // v35.69 — layout provides authed Navbar; avoid double nav
  const [form, setForm] = useState(PRESETS[0].cfg);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rates, setRates] = useState(null);
  const [ciSnippet, setCiSnippet] = useState(null);
  const [ciFormat, setCiFormat] = useState('github-actions');

  useEffect(() => {
    document.title = 'Egress Cost Predictor · Stop egress surprises before they hit your bill | CARTIEAI';
    // Auto-run the first preset on load so the page is never empty
    runPredict(PRESETS[0].cfg);
    axios.get(`${API_URL}/egress/rates`).then((r) => setRates(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/egress/ci-snippet?format=${ciFormat}`).then((r) => setCiSnippet(r.data)).catch(() => {});
  }, [ciFormat]);

  const runPredict = async (cfg) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/egress/predict`, cfg || form);
      setResult(data);
    } catch (e) { /* silent */ }
    finally { setBusy(false); }
  };

  const fmt = (n) => (n == null ? '—' : `$${(+n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`);
  const fmtTokens = (n) => (n == null ? '—' : `${(+n / 1_000_000).toFixed(1)}M`);

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100 relative overflow-hidden" data-testid="egress-page">
      <Helmet><title>Egress Cost Predictor · CARTIEAI</title></Helmet>
      {!user && <PublicNavbar />}

      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-10 left-1/4 w-[420px] h-[420px] rounded-full bg-emerald-500/10 blur-[120px]" />
        <div className="absolute top-40 right-1/4 w-[380px] h-[380px] rounded-full bg-teal-500/10 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 relative">
        {/* HERO */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-300 border border-emerald-500/40 rounded-md px-2 py-1 mb-5 bg-emerald-500/10">
            <Network className="w-3 h-3" /> Free tool · No signup
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white" data-testid="egress-h1">
            Egress Cost <span className="text-emerald-400">Predictor</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed">
            In 2026, <strong className="text-white">data movement</strong> between clouds often costs <strong className="text-emerald-300">more than the AI inference itself.</strong> Teams pick a model on token price and get killed by network charges.
          </p>
          <p className="mt-3 text-sm text-slate-400">
            This pre-flight predictor flashes a warning <strong className="text-white">before</strong> you build a $10k/mo cross-cloud pipeline. Rates pulled from public price pages (AWS $0.09/GB to internet · GCP $0.12/GB to other-cloud · Snowflake $40/TB).
          </p>
        </div>

        {/* PRESETS */}
        <div className="mt-10">
          <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-300 mb-2">Try a preset</div>
          <div className="grid sm:grid-cols-2 gap-2" data-testid="egress-presets">
            {PRESETS.map((p, i) => (
              <button
                key={i}
                onClick={() => { setForm(p.cfg); runPredict(p.cfg); }}
                className="text-left rounded-lg border border-white/10 bg-slate-900/60 hover:border-emerald-500 hover:bg-emerald-500/5 px-4 py-3 text-sm text-slate-200 transition-all"
                data-testid={`preset-${i}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* MAIN GRID */}
        <div className="mt-8 grid lg:grid-cols-5 gap-6">
          {/* FORM */}
          <div className="lg:col-span-2 rounded-xl border border-white/10 bg-slate-900/60 p-5 h-fit" data-testid="egress-form">
            <h2 className="text-lg font-bold text-white mb-3 tracking-tight">Predict</h2>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Source cloud" testid="src">
                <select value={form.source_cloud} onChange={(e) => setForm({ ...form, source_cloud: e.target.value })} className="bg-slate-950 border border-white/10 rounded-md px-2 py-1.5 text-sm text-slate-200 w-full" data-testid="egress-src">
                  <option value="aws">AWS</option>
                  <option value="gcp">GCP</option>
                  <option value="azure">Azure</option>
                  <option value="snowflake">Snowflake</option>
                </select>
              </FormField>
              <FormField label="Dest cloud" testid="dst">
                <select value={form.dest_cloud} onChange={(e) => setForm({ ...form, dest_cloud: e.target.value })} className="bg-slate-950 border border-white/10 rounded-md px-2 py-1.5 text-sm text-slate-200 w-full" data-testid="egress-dst">
                  <option value="aws">AWS</option>
                  <option value="gcp">GCP</option>
                  <option value="azure">Azure</option>
                  <option value="same-region">Same region</option>
                  <option value="internet">Internet</option>
                </select>
              </FormField>
              <FormField label="Data per run (GB)" testid="gb">
                <input type="number" value={form.data_gb} onChange={(e) => setForm({ ...form, data_gb: Number(e.target.value) })} className="bg-slate-950 border border-white/10 rounded-md px-2 py-1.5 text-sm text-slate-200 w-full font-mono" data-testid="egress-gb" />
              </FormField>
              <FormField label="Runs / month" testid="runs">
                <input type="number" value={form.monthly_runs} onChange={(e) => setForm({ ...form, monthly_runs: Number(e.target.value) })} className="bg-slate-950 border border-white/10 rounded-md px-2 py-1.5 text-sm text-slate-200 w-full font-mono" data-testid="egress-runs" />
              </FormField>
              <FormField label="Model" testid="model" colSpan>
                <select value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="bg-slate-950 border border-white/10 rounded-md px-2 py-1.5 text-sm text-slate-200 w-full" data-testid="egress-model">
                  <option value="gpt-5.2">gpt-5.2</option>
                  <option value="claude-sonnet-4-5">claude-sonnet-4-5</option>
                  <option value="claude-opus-4-5">claude-opus-4-5</option>
                  <option value="gemini-3-pro">gemini-3-pro</option>
                  <option value="gemini-3-flash">gemini-3-flash</option>
                </select>
              </FormField>
              <FormField label="Inference token budget (% of data)" testid="tokens-pct" colSpan>
                <input type="number" step="0.1" value={form.inference_token_budget_pct} onChange={(e) => setForm({ ...form, inference_token_budget_pct: Number(e.target.value) })} className="bg-slate-950 border border-white/10 rounded-md px-2 py-1.5 text-sm text-slate-200 w-full font-mono" data-testid="egress-token-pct" />
              </FormField>
            </div>
            <button
              onClick={() => runPredict()}
              disabled={busy}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-5 py-2.5 text-sm shadow-md shadow-emerald-500/20 transition-all"
              data-testid="egress-run"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
              {busy ? 'Computing…' : 'Predict cost'}
            </button>
          </div>

          {/* RESULT */}
          <div className="lg:col-span-3" data-testid="egress-result">
            {result ? (
              <>
                {/* Verdict banner */}
                {(() => {
                  const v = VERDICT_STYLES[result.verdict_color] || VERDICT_STYLES.emerald;
                  const Ic = v.Icon;
                  return (
                    <div className={`rounded-xl border p-5 ${v.card}`} data-testid="verdict-banner">
                      <div className={`flex items-center gap-2 text-sm font-mono uppercase tracking-widest font-bold ${v.label}`}>
                        <Ic className="w-4 h-4" />
                        <span data-testid="verdict-text">{result.verdict}</span>
                      </div>
                      <p className={`mt-2 text-base font-semibold ${v.text}`} data-testid="verdict-message">{result.verdict_message}</p>
                      <ul className="mt-3 space-y-1 text-sm text-slate-300">
                        {result.suggested_actions.map((a, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <ArrowRight className="w-3 h-3 mt-1 shrink-0 text-emerald-400" />
                            <span>{a}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}

                {/* Three-column breakdown */}
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Stat label="Egress / month" icon={Network} value={fmt(result.egress.cost_monthly_usd)} sub={`${result.totals.egress_share_pct}% of total · $${result.egress.rate_per_gb_usd}/GB`} testid="stat-egress" accent="amber" />
                  <Stat label="Inference / month" icon={Cpu} value={fmt(result.inference.cost_monthly_usd)} sub={`${result.totals.inference_share_pct}% of total · ${fmtTokens(result.inference.tokens_per_run)} tokens/run`} testid="stat-inference" accent="emerald" />
                  <Stat label="Total / month" icon={DollarSign} value={fmt(result.totals.monthly_usd)} sub={`${result.input.monthly_runs} runs × $${result.totals.per_run_usd.toFixed(2)}`} testid="stat-total" accent="white" />
                </div>

                {/* 100% stacked bar */}
                <div className="mt-4 rounded-xl border border-white/10 bg-slate-900/60 p-4" data-testid="stack-bar">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-300 mb-2">Cost composition per run</div>
                  <div className="flex h-8 rounded-md overflow-hidden border border-white/10">
                    <div className="bg-amber-500 flex items-center justify-center text-[11px] font-bold text-slate-950" style={{ width: `${Math.max(result.totals.egress_share_pct, 2)}%` }} data-testid="bar-egress">
                      {result.totals.egress_share_pct > 8 && `${result.totals.egress_share_pct}% egress`}
                    </div>
                    <div className="bg-emerald-500 flex items-center justify-center text-[11px] font-bold text-slate-950" style={{ width: `${Math.max(result.totals.inference_share_pct, 2)}%` }} data-testid="bar-inference">
                      {result.totals.inference_share_pct > 8 && `${result.totals.inference_share_pct}% inference`}
                    </div>
                  </div>
                </div>

                {/* Input recap */}
                <div className="mt-4 rounded-xl border border-white/10 bg-slate-900/60 p-4 text-xs text-slate-400 font-mono" data-testid="input-recap">
                  <span className="uppercase tracking-widest text-emerald-300">Pipeline</span>{' '}
                  <span className="text-white font-bold">{result.input.source_cloud.toUpperCase()}</span> → <span className="text-white font-bold">{result.input.dest_cloud.toUpperCase()}</span>{' '}
                  · {result.input.data_gb} GB · {result.input.token_budget_pct}% token budget · model <span className="text-white">{result.input.model}</span>
                </div>
              </>
            ) : (
              <div className="h-64 flex items-center justify-center rounded-xl border border-dashed border-white/10 bg-slate-900/40">
                <Loader2 className="w-6 h-6 animate-spin text-slate-600" />
              </div>
            )}
          </div>
        </div>

        {/* RATE TRANSPARENCY */}
        {rates && (
          <div className="mt-12 rounded-xl border border-white/10 bg-slate-900/60 p-5" data-testid="rate-table">
            <div className="flex items-baseline justify-between gap-2 flex-wrap mb-3">
              <h3 className="text-lg font-bold text-white tracking-tight">Egress rates ($/GB · Q1 2026)</h3>
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-300">
                Transparency · {rates.source}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                    <th className="text-left py-2 pr-3">From</th>
                    <th className="text-left py-2 pr-3">To</th>
                    <th className="text-right py-2">$/GB</th>
                  </tr>
                </thead>
                <tbody className="text-slate-300 font-mono">
                  {Object.entries(rates.egress_per_gb_usd).map(([k, v]) => {
                    const [from, to] = k.split('→');
                    return (
                      <tr key={k} className="border-b border-white/5">
                        <td className="py-1.5 pr-3 text-white">{from.toUpperCase()}</td>
                        <td className="py-1.5 pr-3 text-slate-300">{to.toUpperCase()}</td>
                        <td className="py-1.5 text-right">${v}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* USE IN PRODUCTION — Proxy header + CI snippet */}
        <div className="mt-12 grid lg:grid-cols-2 gap-6" data-testid="egress-prod-wiring">
          {/* Proxy header */}
          <div className="rounded-xl border border-emerald-500/30 bg-slate-900/60 p-5">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <h3 className="text-lg font-bold text-white tracking-tight">Wire into the LLM proxy</h3>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-emerald-300 border border-emerald-500/40 rounded-md px-2 py-1 bg-emerald-500/10">
                ● Live in /api/llm-proxy
              </span>
            </div>
            <p className="text-xs text-slate-400 mb-3 leading-relaxed">
              Pass 3 headers on any proxy call. If the predicted verdict is rose ("MOVE THE MODEL"), the proxy returns <code className="text-amber-300 font-mono">412 Precondition Failed</code> <em>before</em> forwarding to OpenAI / Anthropic / Gemini / Groq / Mistral — saving you both the egress and the inference cost.
            </p>
            <pre className="rounded-lg border border-emerald-500/20 bg-slate-950/80 p-4 text-[11px] leading-relaxed font-mono text-emerald-200 overflow-x-auto whitespace-pre" data-testid="egress-proxy-snippet">
{`curl -X POST https://proxy.cartieai.com/v1/chat/completions \\
  -H "Authorization: Bearer $OPENAI_KEY" \\
  -H "X-Cartie-Key: $CARTIE_KEY" \\
  -H "X-Cartie-Source-Cloud: gcp" \\
  -H "X-Cartie-Dest-Cloud: aws" \\
  -H "X-Cartie-Data-GB: 1024" \\
  -H "X-Cartie-Token-Budget-Pct: 0.05" \\
  -H "X-Cartie-Egress-Enforce: true" \\
  -d '{"model":"gemini-3-flash","messages":[...]}'

# Response when egress-dominant + enforce=true:
# HTTP/1.1 412 Precondition Failed
# { "error":"egress_preflight_blocked", "verdict":"MOVE THE MODEL", ... }`}
            </pre>
          </div>

          {/* CI/CD snippet */}
          <div className="rounded-xl border border-amber-500/30 bg-slate-900/60 p-5">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <h3 className="text-lg font-bold text-white tracking-tight">PR-time gate</h3>
              <div className="flex gap-1" data-testid="egress-ci-tabs">
                <button onClick={() => setCiFormat('github-actions')} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded ${ciFormat === 'github-actions' ? 'bg-amber-500/20 text-amber-200 border border-amber-500/40' : 'text-slate-400 hover:text-amber-300'}`} data-testid="ci-tab-github">GitHub Actions</button>
                <button onClick={() => setCiFormat('gitlab-ci')} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded ${ciFormat === 'gitlab-ci' ? 'bg-amber-500/20 text-amber-200 border border-amber-500/40' : 'text-slate-400 hover:text-amber-300'}`} data-testid="ci-tab-gitlab">GitLab CI</button>
              </div>
            </div>
            <p className="text-xs text-slate-400 mb-3 leading-relaxed">
              Blocks any PR that adds an egress-dominant cross-cloud pipeline. Posts <code className="text-amber-300 font-mono">/api/egress/preflight-plan</code> with the Terraform plan diff → aggregate verdict → CI exits non-zero on rose.
            </p>
            <pre className="rounded-lg border border-amber-500/20 bg-slate-950/80 p-4 text-[11px] leading-relaxed font-mono text-amber-100 overflow-x-auto whitespace-pre max-h-72" data-testid="egress-ci-snippet">
              {ciSnippet?.code || '# Loading…'}
            </pre>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 rounded-2xl border border-emerald-500/30 bg-slate-900/60 p-8 text-center shadow-lg shadow-emerald-500/10" data-testid="egress-cta">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-emerald-500 shadow-md shadow-emerald-500/30 mx-auto mb-3">
            <Database className="w-6 h-6 text-slate-950" />
          </div>
          <h3 className="text-2xl font-extrabold text-white tracking-tight">Want this as a real-time gate on every pipeline?</h3>
          <p className="text-slate-300 mt-2 max-w-xl mx-auto">CARTIE wires the predictor into your PR checks. Cross-cloud egress over 30% of total cost? Merge is blocked.</p>
          <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/signup" className="px-6 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm shadow-md shadow-emerald-500/20 transition-all" data-testid="egress-signup">
              Start free pilot <ArrowRight className="w-4 h-4 inline ml-1" />
            </Link>
            <Link to="/patent-portfolio" className="px-6 py-2.5 rounded-lg border border-white/15 hover:border-emerald-500 hover:text-emerald-300 text-slate-300 text-sm font-semibold transition-all">
              See all {ENGINE_COUNT} engines
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const FormField = ({ label, children, colSpan }) => (
  <div className={colSpan ? 'col-span-2' : ''}>
    <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400 font-bold block mb-1">{label}</label>
    {children}
  </div>
);

const Stat = ({ label, value, sub, icon: Icon, accent, testid }) => {
  const accentCls = accent === 'amber' ? 'text-amber-300' : accent === 'emerald' ? 'text-emerald-300' : 'text-white';
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/60 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-400 font-bold">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`mt-1 text-2xl font-extrabold font-mono ${accentCls}`}>{value}</div>
      <div className="text-[10px] text-slate-500 mt-1">{sub}</div>
    </div>
  );
};

export default EgressPredictor;
